import { useContext, useEffect } from "react";
import { isEmpty } from "lodash";
import { Badge, Modal, ModalBody } from "reactstrap";
import { useSelector } from "react-redux";

import SiteTitle from "../../../helpers/SiteTitle/SiteTitle";
import Tooltip from "../../../components/Tooltip/Tooltip";
import PaginationComponent from "../../../components/Pagination/Pagination";
import TableFilter from "../../../components/TableFilter/TableFilter";
import Button from "../../../components/Button/Button";
import Icon from "../../../components/Icon/Icon";
import UserAvatar from "../../../components/UserAvatar/UserAvatar";
import InfoItem from "./../../../helpers/InfoItem/InfoItem";
import SearchTable from "../../../components/SearchTable/SearchTable";
import PageHeader from "../../../components/PageHeader/PageHeader";
import UserInfoItem from "../../../components/UserInfoItem/UserInfoItem";
import { Block } from "../../../components/Block/Block";
import {
  DataTable,
  DataTableBody,
  DataTableHead,
  DataTableItem,
  DataTableRow,
} from "../../../components/DataTable/DataTable";
import { areYouSureMessage } from "../../../utils/toastMessage/toastMessage";
import { logAdminContext } from "../../../context/AdminContext/LogAdminContext/LogAdminContext";
import { convertDate } from "../../../utils";
import { Col, Row } from "./../../../components/Grid/Grid";
import {
  convertLogMethodToColor,
  convertLogMethodToText,
  convertLogUrlToText,
} from "../../../utils/enum";
import SpecialIcon from "../../../helpers/SpecialIcon/SpecialIcon";

const LogsAdmin = () => {
  const {
    logs,
    log,
    setLog,
    sort,
    setSort,
    start,
    setStart,
    end,
    setEnd,
    pageCount,
    itemsCount,
    setItemsCount,
    pageNumber,
    setPageNumber,
    handleGetLogs,
    handleDeleteLog,
    handleDeleteAllLog,
  } = useContext(logAdminContext);

  useEffect(() => {
    handleGetLogs();
  }, [start, end, pageNumber, itemsCount, sort]);

  return (
    <>
      <SiteTitle title="رویدادنگاری" />

      <PageHeader title="رویدادنگاری" />

      <TableFilter
        setItemsCount={setItemsCount}
        itemsCount={itemsCount}
        setSort={setSort}
        sort={sort}
        setStart={setStart}
        start={start}
        setEnd={setEnd}
        end={end}
      />

      <Block>
        <DataTable className="card-stretch">
          <SearchTable
            firstItem={
              !isEmpty(logs) && (
                <Button
                  color="danger"
                  className="btn-icon"
                  onClick={() =>
                    areYouSureMessage(
                      handleDeleteAllLog,
                      `آیا از حذف همه رویدادنگاری مطمئنید؟`
                    )
                  }
                >
                  <Icon name="trash" />
                </Button>
              )
            }
          />
          <DataTableBody className="is-compact">
            <DataTableHead>
              <DataTableRow>
                <span className="sub-text">#</span>
              </DataTableRow>
              <DataTableRow>
                <span className="sub-text">کاربر</span>
              </DataTableRow>
              <DataTableRow size="md">
                <span className="sub-text">بخش</span>
              </DataTableRow>
              <DataTableRow size="md">
                <span className="sub-text">عملیات</span>
              </DataTableRow>
              <DataTableRow size="lg">
                <span className="sub-text">تاریخ</span>
              </DataTableRow>
              <DataTableRow />
            </DataTableHead>
            {/*Head*/}
            {logs.length > 0
              ? logs.map((item, index) => {
                  return (
                    <DataTableItem key={item._id}>
                      <DataTableRow>
                        <span>{index + 1}</span>
                      </DataTableRow>
                      <DataTableRow>
                        <div className="user-card">
                          <UserAvatar
                            {...(item?.user?.avatar
                              ? { image: item?.user?.avatar }
                              : {
                                  icon: "user-alt",
                                })}
                          />
                          <UserInfoItem
                            title={`${item?.user?.firstName} ${item?.user?.lastName}`}
                            subTitle={item?.user?.phoneNumber}
                          />
                        </div>
                      </DataTableRow>
                      <DataTableRow size="md">
                        <span>{convertLogUrlToText(item.pathname)}</span>
                      </DataTableRow>
                      <DataTableRow size="md">
                        <Badge
                          className="badge-dim"
                          color={convertLogMethodToColor(item.method)}
                        >
                          {convertLogMethodToText(item.method)}
                        </Badge>
                      </DataTableRow>
                      <DataTableRow size="lg">
                        <span>
                          {convertDate(item.createdAt, "YYYY/MM/DD - HH:mm:ss")}
                        </span>
                      </DataTableRow>
                      <DataTableRow className="nk-tb-col-tools">
                        <ul className="nk-tb-actions gx-1">
                          <li
                            onClick={() => {
                              setLog(item);
                            }}
                          >
                            <Tooltip
                              tag="a"
                              containerClassName="btn btn-trigger btn-icon"
                              id={"details" + item._id}
                              icon="eye"
                              direction="top"
                              text="جزئیات"
                            />
                          </li>
                          <li
                            onClick={() =>
                              areYouSureMessage(
                                () => handleDeleteLog(item._id),
                                `آیا از حذف رویداد ${item?.user?.firstName} ${item?.user?.lastName} مطمئنید؟`
                              )
                            }
                          >
                            <Tooltip
                              tag="a"
                              containerClassName="btn btn-trigger btn-icon text-danger"
                              id={"delete" + item._id}
                              icon="trash-fill"
                              direction="top"
                              text="حذف"
                            />
                          </li>
                        </ul>
                      </DataTableRow>
                    </DataTableItem>
                  );
                })
              : null}
          </DataTableBody>
          <div className="card-inner">
            {logs.length > 0 ? (
              <PaginationComponent
                pageCount={pageCount}
                currentPage={pageNumber}
                setPageNumber={setPageNumber}
              />
            ) : (
              <div className="text-center">
                <span className="text-silent">داده ای برای نمایش یافت نشد</span>
              </div>
            )}
          </div>
        </DataTable>
      </Block>

      <Modal
        isOpen={!isEmpty(log)}
        toggle={() => setLog({})}
        className="modal-dialog-centered"
        size="md"
      >
        <ModalBody>
          <span type="button" onClick={() => setLog({})} className="close">
            <Icon name="cross-sm" />
          </span>
          <div className="p-2">
            <h5 className="title">جزئیات عملیات</h5>
            <br />
            <span className="text-primary border-bottom fw-bold">
              اطلاعات عملیات
            </span>
            <Row>
              <Col sm="6">
                <InfoItem title="بخش">
                  {convertLogUrlToText(log.pathname)}
                </InfoItem>
              </Col>
              <Col sm="6">
                <InfoItem title="عملیات">
                  <Badge
                    className="badge-dim"
                    color={convertLogMethodToColor(log.method)}
                  >
                    {convertLogMethodToText(log.method)}
                  </Badge>
                </InfoItem>
              </Col>
              <Col size="12">
                <InfoItem title="مسیر">{log.pathname}</InfoItem>
              </Col>
              <Col size="12">
                <InfoItem title="آدرس">{log.url}</InfoItem>
              </Col>
              {log?.description && (
                <Col size="12">
                  <InfoItem title="توضیحات">{log.description}</InfoItem>
                </Col>
              )}
            </Row>
            <span className="text-primary border-bottom fw-bold">
              اطلاعات کاربر
            </span>
            <Row>
              <Col sm="6">
                <InfoItem title="نام">
                  {log?.user?.firstName} {log?.user?.isVIP && <SpecialIcon />}
                </InfoItem>
              </Col>
              <Col sm="6">
                <InfoItem title="نام خانوادگی">{log?.user?.lastName}</InfoItem>
              </Col>
              <Col sm="6">
                <InfoItem title="تاریخ تولد">
                  {convertDate(log?.user?.birthDate)}
                </InfoItem>
              </Col>
              <Col sm="6">
                <InfoItem title="شماره موبایل">
                  {log?.user?.phoneNumber}
                </InfoItem>
              </Col>
              <Col sm="6">
                <InfoItem title="وضعیت">
                  <Badge
                    className="badge-dot"
                    color={log?.user?.isActive ? "success" : "danger"}
                  >
                    {log?.user?.isActive ? "فعال" : "غیرفعال"}
                  </Badge>
                </InfoItem>
              </Col>
            </Row>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

export default LogsAdmin;
